import React, { useState, useEffect, useRef } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import VisitorService from '../../utils/VisitorTracker';
import '../styles/admin.css';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalUsers: 0,
    totalOrders: 0,
    totalMessages: 0,
    revenue: 0,
    categories: 0,
    todayVisitors: 0,
    weekVisitors: 0,
    todayPageViews: 0,
    monthlyVisitors: 0,
  });
  const [dailyStats, setDailyStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const autoRefreshRef = useRef(null);

  // Initial load
  useEffect(() => {
    fetchDashboardStats();
  }, []);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    if (!autoRefresh) {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
      }
      return;
    }

    // Refresh immediately
    const handleAutoRefresh = () => {
      fetchDashboardStats();
    };

    // Set interval for auto-refresh (10 seconds)
    autoRefreshRef.current = setInterval(handleAutoRefresh, 10000);

    return () => {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
      }
    };
  }, [autoRefresh]);

  const fetchDashboardStats = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      // Fetch real products data
      const productsRes = await fetch(`${API_URL}/api/products`, {
        headers: { 'Accept': 'application/json' },
      });
      const products = await productsRes.json();
      const totalProducts = Array.isArray(products) ? products.length : 0;

      // Calculate total revenue from products
      const revenue = Array.isArray(products)
        ? products.reduce((sum, p) => {
            const price = parseFloat(p.price?.toString().replace(/[^\d.]/g, '') || 0);
            return sum + price;
          }, 0)
        : 0;

      // Get unique categories
      const categories = Array.isArray(products)
        ? [...new Set(products.map((p) => p.category))].length
        : 0;

      // Get visitor analytics
      const todayVisitors = VisitorService.getTodayVisitors();
      const weekVisitors = VisitorService.getVisitorsLastWeek();
      const todayPageViews = VisitorService.getTodayPageViews();
      const monthlyVisitors = VisitorService.getVisitorsLastMonth();
      const dailyStats = VisitorService.getDailyStats(7);

      setStats({
        totalProducts,
        totalUsers: 128, // Mock - would need users endpoint
        totalOrders: 342, // Mock - would need orders endpoint
        totalMessages: 89, // Mock - would need contact messages endpoint
        revenue: Math.round(revenue),
        categories,
        todayVisitors,
        weekVisitors,
        todayPageViews,
        monthlyVisitors,
      });
      
      setDailyStats(dailyStats);
      setError('');
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError('ᠠᠤᠢᠤᠯ᠋ᠠᠵ ᠢᠳᠡᠪᠷᠢ ᠠᠯᠢᠰᠬᠠᠨᠥ');
      // Set mock data on error
      setStats({
        totalProducts: 0,
        totalUsers: 128,
        totalOrders: 342,
        totalMessages: 89,
        revenue: 0,
        categories: 0,
        todayVisitors: 0,
        weekVisitors: 0,
        todayPageViews: 0,
        monthlyVisitors: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleManualRefresh = () => {
    setLoading(true);
    fetchDashboardStats();
  };

  return (
    <div className="admin-container">
      <AdminSidebar />
      <div className="admin-content">
        <div className="admin-header">
          <h1 className="admin-header-title">📊 Dashboard</h1>
          <div className="admin-header-user">
            <button 
              onClick={handleManualRefresh} 
              className="admin-btn admin-btn-secondary"
              style={{ marginRight: '15px' }}
              disabled={loading}
            >
              {loading ? '⏳ Ачааллаж байна...' : '🔄 шинэчлэх'}
            </button>
            
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              marginRight: '15px',
              color: '#ddd',
              cursor: 'pointer'
            }}>
              <input 
                type="checkbox" 
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ fontSize: '12px' }}>🔄 Auto</span>
            </label>

            <span style={{ fontSize: '12px', color: '#999' }}>
              Сүүлийн шинэчлэлт: {lastRefresh.toLocaleTimeString()}
            </span>
            
            <span className="admin-header-user-name">Админ</span>
          </div>
        </div>

        <div className="admin-main">
          {error && (
            <div className="alert alert-danger" style={{ marginBottom: '20px' }}>
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {loading && !dailyStats.length ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#ddd' }}>
              <p>📊 Мэдээллэл ачааллаж байна...</p>
            </div>
          ) : (
            <>
              {/* Product Stats */}
              <div className="dashboard-grid">
                <div className="stat-card">
                  <div className="stat-card-icon">📦</div>
                  <div className="stat-card-value">{stats.totalProducts}</div>
                  <div className="stat-card-label">Нийт Бүтээгдэхүүн</div>
                </div>

                <div className="stat-card">
                  <div className="stat-card-icon">🏷️</div>
                  <div className="stat-card-value">{stats.categories || 0}</div>
                  <div className="stat-card-label">Ангилал</div>
                </div>

              </div>

              {/* Visitor Analytics */}
              <div style={{ marginTop: '30px' }}>
                <h3 style={{ color: '#000', marginBottom: '20px' }}>👥 Сайтийн Зочин Анализ</h3>
                <div className="dashboard-grid">
                  <div className="stat-card">
                    <div className="stat-card-icon">👁️</div>
                    <div className="stat-card-value" style={{ fontSize: '2.5em', color: '#F45325' }}>
                      {stats.todayVisitors}
                    </div>
                    <div className="stat-card-label">Өнөөдрийн Зочин</div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-card-icon">📊</div>
                    <div className="stat-card-value" style={{ fontSize: '2.5em', color: '#F45325' }}>
                      {stats.weekVisitors}
                    </div>
                    <div className="stat-card-label">энэ 7 хоногийн Зочин</div>
                  </div>


                  <div className="stat-card">
                    <div className="stat-card-icon">📅</div>
                    <div className="stat-card-value" style={{ fontSize: '2.5em', color: '#F45325' }}>
                      {stats.monthlyVisitors}
                    </div>
                    <div className="stat-card-label">Сүүлийн Сарын Хандалт</div>
                  </div>
                </div>
              </div>

              {/* Daily Visitor Chart */}
              {dailyStats.length > 0 && (
                <div style={{ marginTop: '30px' }}>
                  <h3 style={{ color: '#000', marginBottom: '20px' }}>📈 Сүүлийн 7 Өдрийн Зочин</h3>
                  <div style={{ background: 'rgba(53, 51, 51, 0.5)', padding: '20px', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', height: '200px', justifyContent: 'space-around' }}>
                      {dailyStats.map((stat, idx) => {
                        const maxVisitors = Math.max(...dailyStats.map((s) => s.visitors), 1);
                        const heightPercent = (stat.visitors / maxVisitors) * 100;
                        
                        return (
                          <div key={idx} style={{ textAlign: 'center', flex: 1 }}>
                            <div
                              style={{
                                height: `${heightPercent}%`,
                                background: 'linear-gradient(180deg, #F45325, #FF6B4E)',
                                borderRadius: '4px 4px 0 0',
                                marginBottom: '10px',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer',
                                minHeight: '20px',
                              }}
                              title={`${stat.day}: ${stat.visitors} зочин`}
                            >
                              <span style={{ fontSize: '12px', color: '#fff', display: 'block', paddingTop: '5px' }}>
                                {stat.visitors}
                              </span>
                            </div>
                            <p style={{ fontSize: '12px', color: '#ddd', margin: '5px 0 0' }}>
                              {stat.day}
                            </p>
                            <p style={{ fontSize: '10px', color: '#999', margin: '2px 0 0' }}>
                              {new Date(stat.date).getDate()}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Product Statistics */}
              <div style={{ marginTop: '30px' }}>
                <h3 style={{ color: '#000', marginBottom: '20px' }}>📦 Бүтээгдэхүүний Мэдээлэл</h3>
                <div style={{ background: 'rgba(53, 51, 51, 0.5)', padding: '20px', borderRadius: '8px' }}>
                  <p style={{ color: '#ddd', marginBottom: '10px' }}>
                    <strong style={{ color: '#F45325' }}>Нийт:</strong> {stats.totalProducts} бүтээгдэхүүн
                  </p>
                  <p style={{ color: '#ddd', marginBottom: '10px' }}>
                    <strong style={{ color: '#F45325' }}>Ангилал:</strong> {stats.categories || 0} категори
                  </p>
                  <p style={{ color: '#ddd' }}>
                    <strong style={{ color: '#F45325' }}>Нийт Үнэ:</strong> ₮{(stats.revenue / 1000000).toFixed(2)}M
                  </p>
                </div>
              </div>

              {/* Debug Info */}
              <div style={{ marginTop: '30px', marginBottom: '30px' }}>
                <details style={{ background: 'rgba(53, 51, 51, 0.5)', padding: '15px', borderRadius: '8px', color: '#ddd' }}>
                  <summary style={{ cursor: 'pointer', color: '#F45325', fontWeight: 'bold' }}>
                    🔍 Debug Info (for troubleshooting)
                  </summary>
                  <pre style={{ 
                    marginTop: '15px', 
                    background: 'rgba(0,0,0,0.3)',
                    padding: '15px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    overflow: 'auto',
                    color: '#0f0'
                  }}>
{JSON.stringify(VisitorService.getDebugInfo(), null, 2)}
                  </pre>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(VisitorService.getDebugInfo(), null, 2));
                      alert('Clipboard-руу хуулагдлаа!');
                    }}
                    style={{
                      marginTop: '10px',
                      padding: '8px 16px',
                      background: '#F45325',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Copy Debug Info
                  </button>
                </details>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
